import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import AddEditProductModal from './AddEditProductModal';
import { useTranslation } from '../context/TranslationContext';
import { formatWeight } from '../utils/formatWeight';
import { formatPrice } from '../utils/formatPrice';
import { localizedField } from '../utils/localizedField';
import { Button, ActionIcon, TextInput, Text, Modal, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronDown, IconChevronRight, IconPlus, IconEyeOff, IconEye, IconStar, IconStarFilled, IconEdit, IconTrash, IconSettings } from '@tabler/icons-react';
import { categoryPrefix } from '../utils/skuPreview';
import './AdminProducts.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function getFirstImage(images) {
  if (!images) return null;
  let arr = images;
  if (typeof images === 'string') {
    try { arr = JSON.parse(images); } catch { return null; }
  }
  if (!Array.isArray(arr) || !arr.length) return null;
  const id = arr[0];
  if (!id) return null;
  return id.startsWith('http') ? id : `${API_BASE_URL}/files/${id}`;
}

export default function AdminProducts() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const [modalState, setModalState] = useState({ open: false, mode: null, product: null, parentCode: null });
  const [featured, setFeatured] = useState([]);

  // Product Categories
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [showCategories, setShowCategories] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const [editingCatId, setEditingCatId] = useState(null);
  const [editCatName, setEditCatName] = useState('');
  const [catError, setCatError] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      setCategories(await apiService.getAdminProductCategories());
    } catch (err) {
      console.error(err);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCatName.trim()) return;
    setCatError('');
    setAddingCat(true);
    try {
      await apiService.addProductCategory({ name: newCatName.trim() });
      setNewCatName('');
      await fetchCategories();
    } catch (err) {
      setCatError(err.message || 'Failed to add');
    } finally {
      setAddingCat(false);
    }
  };

  const startEditCategory = (cat) => {
    setEditingCatId(cat.id);
    setEditCatName(cat.name);
    setCatError('');
  };
  const cancelEditCategory = () => { setEditingCatId(null); setCatError(''); };

  const handleUpdateCategory = async (id) => {
    if (!editCatName.trim()) return;
    setCatError('');
    try {
      await apiService.updateProductCategory(id, { name: editCatName.trim() });
      setEditingCatId(null);
      await fetchCategories();
    } catch (err) {
      setCatError(err.message || 'Failed to update');
    }
  };

  const [deletingCat, setDeletingCat] = useState(null);

  const confirmDeleteCategory = () => {
    if (!deletingCat) return;
    apiService.deleteProductCategory(deletingCat.id)
      .then(() => { setDeletingCat(null); return fetchCategories(); })
      .catch((err) => { notifications.show({ title: 'Delete failed', message: err.message || 'Failed to delete', color: 'red' }); setDeletingCat(null); });
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAdminProducts({ lang: t.langCode });
      setProducts(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    apiService.getAdminFeatured().then(setFeatured).catch(() => {});
  }, [t.langCode]);

  const filteredProducts = products.filter(p => {
    const q = searchQuery.toLowerCase();
    const nameStr = localizedField(p, 'name', t.langCode);
    const codeStr = p.product_code || '';
    return nameStr.toLowerCase().includes(q) || codeStr.toLowerCase().includes(q);
  });

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleVariantVisible = async (sku, makeHidden) => {
    try {
      await apiService.hideVariant(sku, makeHidden);
      await fetchProducts();
    } catch (err) {
      console.error(err);
    }
  };

  const openAddParent = () => setModalState({ open: true, mode: 'add-parent', product: null, parentCode: null });
  const openEditParent = (p) => setModalState({ open: true, mode: 'edit-parent', product: p, parentCode: null });
  const openEditVariant = (p, v) => setModalState({ open: true, mode: 'edit-variant', product: v, parentCode: p.product_code });

  const close = () => setModalState({ open: false, mode: null, product: null, parentCode: null });

  const toggleFeatured = async (code) => {
    const prev = featured;
    const next = prev.includes(code)
      ? prev.filter((c) => c !== code)
      : [...prev, code];
    if (next.length === prev.length && !prev.includes(code)) return;
    setFeatured(next);
    try {
      await apiService.updateFeatured(next);
    } catch (err) {
      console.error(err);
      setFeatured(prev);
    }
  };

  return (
    <div className="admin-products dashboard-view-wrapper">
      <header className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-title">Products</h1>
          <p className="dashboard-page-description">Manage products, variants, and catalog.</p>
        </div>
      </header>

      {/* Product Categories Section */}
      <div style={{ marginBottom: '1.5rem', border: '1px solid #e9ecef', borderRadius: 8, overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => setShowCategories(!showCategories)}
          aria-expanded={showCategories}
          aria-controls="product-categories-panel"
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
            background: '#f8f9fa', border: 'none', cursor: 'pointer', fontSize: '0.95rem', fontWeight: 600,
            color: '#2d3748', textAlign: 'left',
          }}
        >
          <IconSettings size={18} aria-hidden="true" />
          Product Categories
          <span style={{ marginLeft: 'auto', fontSize: '0.8rem', fontWeight: 400, color: '#718096' }}>
            {categories.length} total
          </span>
          {showCategories ? <IconChevronDown size={16} aria-hidden="true" /> : <IconChevronRight size={16} aria-hidden="true" />}
        </button>

        {showCategories && (
          <div id="product-categories-panel" style={{ padding: '16px' }} role="region" aria-label="Product categories">
            <form onSubmit={handleAddCategory} style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <TextInput
                placeholder="Category name (e.g. Yogi Paneer)"
                value={newCatName}
                onChange={(e) => setNewCatName(e.currentTarget.value)}
                aria-label="New category name"
                disabled={addingCat}
                style={{ flex: 1, minWidth: 200 }}
              />
              <Button type="submit" color="green" leftSection={<IconPlus size={16} />} loading={addingCat} disabled={!newCatName.trim()}>
                Add
              </Button>
            </form>
            {catError && <Text c="red" size="sm" mb="xs">{catError}</Text>}

            {categoriesLoading ? (
              <Text c="dimmed" size="sm">Loading...</Text>
            ) : categories.length === 0 ? (
              <Text c="dimmed" size="sm">No categories configured.</Text>
            ) : (
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>SKU Prefix</th>
                      <th style={{ width: 120 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((cat) => (
                      <tr key={cat.id}>
                        <td>
                          {editingCatId === cat.id ? (
                            <TextInput value={editCatName} onChange={(e) => setEditCatName(e.currentTarget.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateCategory(cat.id); if (e.key === 'Escape') cancelEditCategory(); }} aria-label="Edit name" size="sm" autoFocus />
                          ) : (
                            <strong>{cat.name}</strong>
                          )}
                        </td>
                        <td><code>{categoryPrefix(cat.name)}</code></td>
                        <td>
                          <div className="inline-actions">
                            {editingCatId === cat.id ? (
                              <>
                                <Button size="xs" variant="filled" onClick={() => handleUpdateCategory(cat.id)}>Save</Button>
                                <Button size="xs" variant="subtle" color="gray" onClick={cancelEditCategory}>Cancel</Button>
                              </>
                            ) : (
                              <>
                                <ActionIcon variant="subtle" color="gray" onClick={() => startEditCategory(cat)} aria-label={`Edit ${cat.name}`} title="Edit"><IconEdit size={16} aria-hidden="true" /></ActionIcon>
                                <ActionIcon variant="subtle" color="red" onClick={() => setDeletingCat(cat)} aria-label={`Delete ${cat.name}`} title="Delete"><IconTrash size={16} aria-hidden="true" /></ActionIcon>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <TextInput
          placeholder="Search products by name or code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 250, maxWidth: 400 }}
          aria-label="Search products"
        />
        <Button color="green" leftSection={<IconPlus size={16} />} onClick={openAddParent}>
          Add Product
        </Button>
      </div>

      {loading ? <p>Loading products...</p> : (
        <div className="table-responsive">
          <table className="admin-table">
            <caption className="sr-only">All products and their variants</caption>
            <thead>
              <tr>
                <th style={{ width: '40px' }} scope="col"><span className="sr-only">Expand</span></th>
                <th style={{ width: '40px' }} scope="col"><span className="sr-only">Featured</span></th>
                <th scope="col">Image</th>
                <th scope="col">Product Code</th>
                <th scope="col">Name</th>
                <th scope="col">Category</th>
                <th scope="col">Variants</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map(p => {
                const isExpanded = expandedRows.has(p.id);
                const variants = p.variants || [];
                return (
                  <ParentRow
                    key={p.id}
                    product={p}
                    isExpanded={isExpanded}
                    variants={variants}
                    langCode={t.langCode}
                    featured={featured}
                    onToggleFeatured={toggleFeatured}
                    onToggle={() => toggleRow(p.id)}
                    onEditParent={() => openEditParent(p)}
                    onEditVariant={(v) => openEditVariant(p, v)}
                    onToggleVariantVisible={handleToggleVariantVisible}
                  />
                );
              })}
              {filteredProducts.length === 0 && (
                <tr><td colSpan="8">No products found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalState.open && (
        <AddEditProductModal
          product={modalState.product}
          mode={modalState.mode}
          parentCode={modalState.parentCode}
          onClose={close}
          onSaved={async () => {
            close();
            await fetchProducts();
          }}
        />
      )}

      <Modal opened={!!deletingCat} onClose={() => setDeletingCat(null)} title="Delete Category" centered>
        <Text size="sm">
          Are you sure you want to delete <strong>{deletingCat?.name}</strong>?
          Products using this category must be reassigned first.
        </Text>
        <Group justify="flex-end" mt="lg">
          <Button variant="subtle" color="gray" onClick={() => setDeletingCat(null)}>Cancel</Button>
          <Button color="red" onClick={confirmDeleteCategory}>Delete</Button>
        </Group>
      </Modal>
    </div>
  );
}

function ParentRow({ product: p, isExpanded, variants, langCode, featured, onToggleFeatured, onToggle, onEditParent, onEditVariant, onToggleVariantVisible }) {
  const nameStr = localizedField(p, 'name', langCode);
  const hiddenCount = variants.filter(v => v.is_hidden).length;
  const visibleCount = variants.length - hiddenCount;

  return (
    <>
      <tr style={{ background: isExpanded ? '#f8f9fa' : undefined }}>
        <td>
          <ActionIcon variant="subtle" onClick={onToggle} aria-label={isExpanded ? 'Collapse row' : 'Expand row'} aria-expanded={isExpanded}>
            {isExpanded ? <IconChevronDown size={18} aria-hidden="true" /> : <IconChevronRight size={18} aria-hidden="true" />}
          </ActionIcon>
        </td>
        <td></td>
        <td>
          {(() => {
            const imgSrc = getFirstImage(p.images);
            return imgSrc ? (
              <img src={imgSrc} alt={nameStr} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px' }} />
            ) : (
              <div style={{ width: 48, height: 48, background: '#eee', borderRadius: 4 }} />
            );
          })()}
        </td>
        <td><strong>{p.product_code}</strong></td>
        <td>
          {nameStr}
          {p.is_bulk_only ? <span className="badge badge-info" style={{ marginLeft: 6 }}>Bulk Only</span> : null}
          {p.is_variable_weight ? <span className="badge badge-warning" style={{ marginLeft: 6 }}>Sold by weight</span> : null}
          {p.is_hidden ? <span className="badge badge-secondary" style={{ marginLeft: 6 }}>Hidden</span> : null}
        </td>
        <td>{p.category}</td>
        <td>
          <span className="badge badge-success">{visibleCount}</span>
          {hiddenCount > 0 ? <span className="badge badge-secondary" style={{ marginLeft: 4 }}>{hiddenCount} hidden</span> : null}
        </td>
        <td>
          <Button variant="subtle" size="sm" leftSection={<IconEdit size={14} aria-hidden="true" />} onClick={onEditParent}>
            Edit Product
          </Button>
        </td>
      </tr>
      {isExpanded && variants.map(v => (
        <VariantRow
          key={v.id || v.sku}
          variant={v}
          langCode={langCode}
          isFeatured={featured.includes(v.sku)}
          onToggleFeatured={() => onToggleFeatured(v.sku)}
          onEdit={() => onEditVariant(v)}
          onToggleVisible={() => onToggleVariantVisible(v.sku, !v.is_hidden)}
        />
      ))}
      {isExpanded && variants.length === 0 && (
        <tr><td colSpan="8" style={{ paddingLeft: 50, color: '#888', fontStyle: 'italic' }}>No variants yet.</td></tr>
      )}
    </>
  );
}

function VariantRow({ variant: v, langCode, isFeatured, onToggleFeatured, onEdit, onToggleVisible }) {
  const weightLabel = v.weight_value != null ? formatWeight(v.weight_value, v.weight_unit || '') : '—';
  const priceLabel = v.price != null ? formatPrice(v.price) : '—';
  const isHidden = !!v.is_hidden;

  return (
    <tr style={{ background: "#fafbfc" }}>
      <td></td>
      <td>
        <ActionIcon
          variant="subtle"
          onClick={onToggleFeatured}
          aria-label={isFeatured ? `Remove ${v.sku} from featured` : `Add ${v.sku} to featured`}
          title={isFeatured ? 'Remove from featured' : 'Add to featured'}
        >
          {isFeatured ? <IconStarFilled size={18} color="#f59f00" aria-hidden="true" /> : <IconStar size={18} color="#adb5bd" aria-hidden="true" />}
        </ActionIcon>
      </td>
      <td>
        <code>{v.sku}</code>
        {v.article_number ? <span style={{ marginLeft: 8, fontSize: 12, color: '#868e96' }}>Art: {v.article_number}</span> : null}
      </td>
      <td style={{ paddingLeft: 32 }}>
        {weightLabel}
        {v.is_default ? (
          <span className="badge badge-info" style={{ marginLeft: 6 }}>
            Default
          </span>
        ) : null}
        {isHidden ? (
          <span className="badge badge-secondary" style={{ marginLeft: 6 }}>
            Hidden
          </span>
        ) : null}
        {v.business_only ? (
          <span className="badge badge-info" style={{ marginLeft: 6 }}>
            Business
          </span>
        ) : null}
      </td>
      <td></td>
      <td>
        <span>{priceLabel}</span>
        <span style={{ marginLeft: 12 }}>
          Stock: {v.stock_quantity != null ? v.stock_quantity : "—"}
        </span>
      </td>
      <td></td>
      <td>
        <Button variant="subtle" size="sm" leftSection={<IconEdit size={14} aria-hidden="true" />} onClick={onEdit} style={{ marginRight: 8 }}>
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          color={isHidden ? "teal" : "gray"}
          leftSection={isHidden ? <IconEye size={14} aria-hidden="true" /> : <IconEyeOff size={14} aria-hidden="true" />}
          onClick={onToggleVisible}
          aria-label={isHidden ? `Show ${v.sku} to customers` : `Hide ${v.sku} from customers`}
          title={isHidden ? 'Make this variant visible to customers' : 'Hide this variant from customers'}
        >
          {isHidden ? 'Show' : 'Hide'}
        </Button>
      </td>
    </tr>
  );
}
